<template>
    <div class="mb-[150px] mt-12 space-y-5">
        <div>
            <h1 class="text-xl font-semibold text-gray-800">
                {{ title }}
            </h1>
            <p class="text-sm text-gray-500">
                {{ description }}
            </p>
        </div>

        <div class="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
            <div class="flex flex-wrap items-end justify-between gap-4">
                <KioskRangePicker
                    :from="from"
                    :to="to"
                    input-id-prefix="activity-logs"
                    @change="applyRange"
                />

                <div class="flex items-center gap-2 pb-1">
                    <span class="text-sm font-medium text-gray-500">
                        {{ t("vue.kiosk_activity_logs_records") }}:
                    </span>
                    <span
                        class="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-0.5 text-sm font-semibold text-indigo-700"
                    >
                        {{ firstRecordIndex }}–{{ lastRecordIndex }} /
                        {{ total }}
                    </span>
                </div>
            </div>

            <ActivityLogFilterBar
                :filters="filters"
                :filter-options="filterOptions"
                @change="applyFilters"
            />
        </div>

        <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
            <ActivityLogTable v-if="logs.length > 0" :items="logs" show-date />
            <p v-else class="p-6 text-sm text-gray-500">
                {{ t("vue.kiosk_activity_logs_empty") }}
            </p>
        </div>

        <div v-if="pagination.last_page > 1" class="flex justify-center">
            <Pagination :items="pagination" @change-page="applyPage" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import Pagination from "@/components/Atomic/DataTable/components/Pagination/Pagination.vue";
import type IPaginationData from "@/components/Atomic/DataTable/types/IPaginationData";
import ActivityLogTable from "./ActivityLogTable.vue";
import ActivityLogFilterBar from "./ActivityLogFilterBar.vue";
import KioskRangePicker from "../../components/KioskRangePicker.vue";
import type {
    IActivityLogFilterOptions,
    IActivityLogFilters,
    IActivityLogItem,
    IActivityLogRange,
} from "../types";

const { t } = useTranslations();

const props = defineProps<{
    title: string;
    description: string;
    from: string;
    to: string;
    page: number;
    perPage: number;
    total: number;
    logs: IActivityLogItem[];
    filters: IActivityLogFilters;
    filterOptions: IActivityLogFilterOptions;
    routeName: string;
    routeParams: Record<string, number>;
}>();

const buildFilterQuery = (
    filters: IActivityLogFilters,
): Record<string, string> => {
    const query: Record<string, string> = {};

    for (const [key, value] of Object.entries(filters)) {
        if (value !== null) {
            query[key] = value;
        }
    }

    return query;
};

const filterQuery = computed(
    (): Record<string, string> => buildFilterQuery(props.filters),
);

const rangeQuery = computed(
    (): Record<string, string> => ({
        from: props.from,
        to: props.to,
    }),
);

const firstRecordIndex = computed((): number =>
    props.logs.length === 0 ? 0 : (props.page - 1) * props.perPage + 1,
);

const lastRecordIndex = computed((): number =>
    props.logs.length === 0
        ? 0
        : (props.page - 1) * props.perPage + props.logs.length,
);

const pagination = computed((): IPaginationData<IActivityLogItem> => {
    const lastPage = Math.max(1, Math.ceil(props.total / props.perPage));

    return {
        data: props.logs,
        total_records: props.total,
        current_page: props.page,
        per_page: props.perPage,
        next_page: props.page < lastPage ? props.page + 1 : undefined,
        prev_page: props.page > 1 ? props.page - 1 : undefined,
        last_page: lastPage,
    };
});

const visit = (query: Record<string, string | number>): void => {
    router.get(route(props.routeName, props.routeParams), query, {
        preserveState: false,
        preserveScroll: true,
    });
};

const applyRange = (range: IActivityLogRange | null): void => {
    visit({ ...(range ?? {}), ...filterQuery.value });
};

const applyFilters = (filters: IActivityLogFilters): void => {
    visit({ ...rangeQuery.value, ...buildFilterQuery(filters) });
};

const applyPage = (newPage: number | undefined): void => {
    if (newPage === undefined) {
        return;
    }

    visit({ ...rangeQuery.value, ...filterQuery.value, page: newPage });
};
</script>
